import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { asEmptyAttribute, EmptyAttribute, InputBooleanAttribute, isTruthyInput } from '../core/empty-attribute';
import { errorMessages } from '../core/error-messages';
import { FormElementCore } from '../core/form-element-core';

export type TextFieldType = 'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url' | 'textarea';

@Component({
  standalone: false,
  selector: 'campus-text-field',
  templateUrl: './text-field.component.html',
  styleUrls: ['./text-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TextFieldComponent,
      multi: true,
    },
    { provide: NG_VALIDATORS, useExisting: TextFieldComponent, multi: true },
  ],
})
export class TextFieldComponent extends FormElementCore<string> implements OnInit, OnChanges {
  public errorText: string | Record<string, string> = errorMessages;

  public supportingTextOrErrorText$: Observable<string>;
  public isAutoCounter: boolean;
  public trailingSupportingOrCounterText: string;

  private _inputRight: boolean;

  @ViewChild('input', { static: false, read: ElementRef }) inputEl: ElementRef<HTMLInputElement>;

  @HostBinding('class')
  defaultClasses = ['text-field', 'inline', 'outline-none'];

  @HostBinding('attr.role')
  @Input()
  role = 'presentation';

  @Input() leadingText: string;
  @Input() trailingText: string;
  @Input() supportingText: string;
  @Input() supportingTextVariant: 'default' | 'warn';
  @Input() trailingSupportingText: string;
  @Input() label: string;
  @Input() rows = 2;
  @Input() matAutocomplete: any;

  @HostBinding('attr.data-type')
  @Input()
  type: TextFieldType;

  @Input() leadingIcon: string;
  @Input() trailingIcon: string;
  @Input() clearable: InputBooleanAttribute;

  @Input() ariaDescribedBy: string;
  @Input() max: number;
  @Input() maxLength: number;
  @Input() min: number;
  @Input() minLength: number;
  @Input() pattern: string;
  @Input() placeholder: string;

  @Input() ariaAutoComplete: 'none' | 'inline' | 'list' | 'both';
  @Input() ariaExpanded: boolean;

  @Input() step: number;
  @Input() truncate: InputBooleanAttribute;

  @HostBinding('attr.data-active')
  @Input()
  active: boolean;

  @Input()
  get inputRight() {
    return this._inputRight;
  }
  set inputRight(value: InputBooleanAttribute) {
    this._inputRight = isTruthyInput(value);
  }

  get element() {
    return this.readOnly ? this.elementRef.nativeElement : this.inputEl?.nativeElement;
  }

  get currentValue() {
    if (this.readOnly) return this.value;
    return this.formControl?.value ?? this.value;
  }

  @Output() clear = new EventEmitter<MouseEvent>();

  @HostBinding('attr.data-populated')
  isPopulated: EmptyAttribute;

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (event.defaultPrevented) return;

    this.element.focus();
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    this.hovered = true;
  }
  @HostListener('mouseleave')
  onMouseLeave() {
    this.hovered = false;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.supportingTextOrErrorText$ = this.getSupportingTextOrErrorText$();
    this.formControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => this.onValueChanged());
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes.trailingSupportingText || changes.maxLength) {
      this.onValueChanged();
    }
    if (changes.clearable) {
      this.clearable = isTruthyInput(changes.clearable.currentValue);
    }
    if (changes.truncate) {
      this.truncate = isTruthyInput(changes.truncate.currentValue);
    }
  }

  handleInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (this.onChange) {
      this.onChange(value);
    } else {
      this.value = value;
    }
  }

  validate(control: FormControl) {
    const errors = super.validate(control);
    const value = control.value;

    if (errors) {
      return errors;
    }

    if (this.required && value === '') {
      return { required: true };
    }

    if (this.type === 'email') {
      if (value?.includes('@') === false) {
        return { missingat: true };
      }

      if (!value?.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]+$/)) {
        return { email: true };
      }
    }

    if (this.type === 'number') {
      if (value && this.min !== undefined && +value < this.min) {
        return { min: true };
      }

      if (value && this.max !== undefined && +value > this.max) {
        return { max: true };
      }
    }

    return null;
  }

  onClear(event: MouseEvent) {
    event.stopPropagation();

    this.value = undefined;
    this.clear.emit(event);
  }

  protected onValueChanged() {
    this.trailingSupportingOrCounterText = this.getTrailingSupportingOrCounterText();
    this.isPopulated = asEmptyAttribute(this.value);
    this.isAutoCounter = this.getIsAutoCounter();
  }

  private getSupportingTextOrErrorText$() {
    return this.status$.pipe(
      map((status) => (status === 'invalid' && this.errorText ? this.getMatchingErrorText() : this.supportingText))
    );
  }

  private getIsAutoCounter() {
    return this.maxLength && !this.trailingSupportingText;
  }
  private getTrailingSupportingOrCounterText() {
    if (this.trailingSupportingText) return this.trailingSupportingText;
    if (this.maxLength)
      return `${this.formControl?.value != null ? this.formControl.value.length : this.value?.length || 0}/${
        this.maxLength
      }`;
  }

  private getMatchingErrorText() {
    if (typeof this.errorText === 'string') {
      return this.errorText;
    }

    if (this.formControl?.errors) {
      for (const key in this.formControl.errors) {
        if (key in this.errorText) {
          const text: string = this.errorText[key];

          const replaced = text.replace(/\{\{(\w+)\}\}/g, (match: string, captured: string) => {
            if (captured === 'value') {
              return this.formControl.value;
            }
            return this[captured].toString();
          });

          return replaced;
        }
      }
    }
  }
}
